import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { kilometersApi, formatApiError } from '../../services/api';
import { Plus, X, Trash, PencilSimple, TrendUp, TrendDown, Minus, Gauge, CalendarBlank, ChartLine, WarningCircle } from '@phosphor-icons/react';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer } from 'recharts';

const MONTH_NAMES = {
  '01': 'Ene', '02': 'Feb', '03': 'Mar', '04': 'Abr',
  '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Ago',
  '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dic'
};

const fmtMonth = (yyyymm) => {
  if (!yyyymm || yyyymm.length < 7) return yyyymm || '-';
  return `${MONTH_NAMES[yyyymm.substring(5, 7)] || yyyymm.substring(5, 7)} ${yyyymm.substring(2, 4)}`;
};

const fmtKm = (n) => new Intl.NumberFormat('es-MX').format(n || 0);

const INITIAL_FORM = {
  vehicle_id: '',
  date: new Date().toISOString().split('T')[0],
  km: '',
  notes: '',
};

const KilometrajeTab = ({ vehicles }) => {
  const [readings, setReadings] = useState([]);
  const [comparison, setComparison] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [chartVehicleIds, setChartVehicleIds] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [rd, cmp] = await Promise.all([
        kilometersApi.getAll(),
        kilometersApi.monthlyComparison(),
      ]);
      setReadings(rd.data);
      setComparison(cmp.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-select up to top 5 vehicles with data for the chart initially
  useEffect(() => {
    if (comparison.length > 0 && chartVehicleIds.length === 0) {
      setChartVehicleIds(comparison.slice(0, 5).map((v) => v.vehicle_id));
    }
  }, [comparison, chartVehicleIds.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.vehicle_id) return setFormError('Selecciona un vehículo');
    if (!form.date) return setFormError('Selecciona una fecha');
    if (form.km === '' || parseInt(form.km, 10) < 0) return setFormError('Kilometraje inválido');
    try {
      setSaving(true);
      const payload = { ...form, km: parseInt(form.km, 10) };
      if (editingId) {
        await kilometersApi.update(editingId, payload);
      } else {
        await kilometersApi.create(payload);
      }
      setShowModal(false);
      setEditingId(null);
      setForm(INITIAL_FORM);
      await load();
    } catch (err) {
      setFormError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (r) => {
    setEditingId(r.id);
    setForm({
      vehicle_id: r.vehicle_id || '',
      date: (r.date || '').substring(0, 10),
      km: r.km !== undefined ? String(r.km) : '',
      notes: r.notes || '',
    });
    setFormError('');
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingId(null);
    setForm(INITIAL_FORM);
    setFormError('');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta lectura de kilometraje?')) return;
    try {
      await kilometersApi.delete(id);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  // Get all months present across all vehicles (union, sorted asc for chart, desc for table)
  const allMonthsAsc = useMemo(
    () => Array.from(new Set(comparison.flatMap((v) => v.series.map((s) => s.month)))).sort(),
    [comparison]
  );
  const allMonths = useMemo(() => [...allMonthsAsc].reverse(), [allMonthsAsc]);

  // Build a flat dataset for recharts: [{month:"Ene 26", [vehicle_name]: km, ...}, ...]
  const chartData = useMemo(() => {
    const selected = comparison.filter((v) => chartVehicleIds.includes(v.vehicle_id));
    return allMonthsAsc.map((m) => {
      const row = { month: fmtMonth(m) };
      selected.forEach((v) => {
        const s = v.series.find((x) => x.month === m);
        if (s) row[v.vehicle_name || v.vehicle_plates || v.vehicle_id] = s.km;
      });
      return row;
    });
  }, [comparison, chartVehicleIds, allMonthsAsc]);

  const chartVehicles = useMemo(() => comparison.filter((v) => chartVehicleIds.includes(v.vehicle_id)), [comparison, chartVehicleIds]);
  const CHART_COLORS = ['#0055FF', '#7C3AED', '#E11D48', '#059669', '#F59E0B', '#0891B2', '#DB2777', '#4F46E5'];

  const toggleChartVehicle = (vid) => {
    setChartVehicleIds((prev) =>
      prev.includes(vid) ? prev.filter((x) => x !== vid) : [...prev, vid]
    );
  };

  const renderChange = (pct, delta) => {
    if (pct === undefined || pct === null) return <span className="text-[#94A3B8] text-xs">—</span>;
    const isUp = delta > 0;
    const isFlat = delta === 0;
    const color = isFlat ? 'text-[#64748B]' : isUp ? 'text-emerald-600' : 'text-red-600';
    const Icon = isFlat ? Minus : isUp ? TrendUp : TrendDown;
    return (
      <span className={`inline-flex items-center gap-1 text-xs font-semibold ${color}`}>
        <Icon size={12} weight="bold" />
        {isUp ? '+' : ''}{pct.toFixed(1)}%
        <span className="text-[#94A3B8] font-normal">({isUp ? '+' : ''}{fmtKm(delta)} km)</span>
      </span>
    );
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div data-testid="kilometers-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-heading font-bold text-[#0F172A]">Kilometraje por Vehículo</h2>
          <p className="text-xs text-[#64748B]">{readings.length} lecturas registradas · Comparativo mes vs mes</p>
        </div>
        <button
          onClick={openCreateModal}
          className="btn-primary flex items-center gap-2"
          data-testid="add-km-btn"
        >
          <Plus size={18} /> Registrar Kilometraje
        </button>
      </div>

      {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}

      {/* Banner alerta km > 9,500 */}
      {comparison.some((v) => v.has_alert) && (
        <div className="mb-4 p-3 rounded-lg border-2 border-red-300 bg-red-50 flex items-center gap-2 text-sm" data-testid="km-alert-banner">
          <WarningCircle size={18} weight="fill" className="text-red-600 flex-shrink-0" />
          <div>
            <strong className="text-red-700">Vehículos con más de 9,500 km recorridos en un mes:</strong>{' '}
            <span className="text-red-600">
              {comparison.filter((v) => v.has_alert).map((v) => v.vehicle_plates || v.vehicle_name).join(', ')}
            </span>
          </div>
        </div>
      )}

      {/* Comparative table */}
      {comparison.length === 0 ? (
        <div className="card p-8 text-center text-[#64748B]">
          Aún no hay lecturas de kilometraje. Registra la primera para comenzar el comparativo mensual.
        </div>
      ) : (
        <>
        <div className="card p-4 mb-6 overflow-x-auto">
          <h3 className="text-sm font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
            <Gauge size={16} className="text-[#0055FF]" /> Comparativo Mensual
          </h3>
          <table className="w-full text-sm">
            <thead className="border-b border-[#E2E8F0]">
              <tr className="text-left">
                <th className="py-2 font-semibold text-[#64748B] text-xs uppercase tracking-wider">Vehículo</th>
                <th className="py-2 font-semibold text-[#64748B] text-xs uppercase tracking-wider">Placas</th>
                <th className="py-2 font-semibold text-[#64748B] text-xs uppercase tracking-wider">Año</th>
                <th className="py-2 font-semibold text-[#64748B] text-xs uppercase tracking-wider">Cliente</th>
                {allMonths.map((m) => (
                  <th key={m} className="py-2 pr-3 text-right font-semibold text-[#64748B] text-xs uppercase tracking-wider whitespace-nowrap">{fmtMonth(m)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparison.map((v) => {
                const seriesByMonth = Object.fromEntries(v.series.map((s) => [s.month, s]));
                return (
                  <tr key={v.vehicle_id} className={`border-b border-[#F1F5F9] hover:bg-[#F8FAFC] ${v.has_alert ? 'bg-red-50/40' : ''}`} data-testid={v.has_alert ? `km-alert-row-${v.vehicle_id}` : undefined}>
                    <td className="py-3 font-medium text-[#0F172A]">
                      <div className="flex items-center gap-2">
                        {v.has_alert && (
                          <span title="Excedió 9,500 km en al menos un mes" className="text-red-600">
                            <WarningCircle size={14} weight="fill" />
                          </span>
                        )}
                        {v.vehicle_name || '—'}
                      </div>
                    </td>
                    <td className="py-3 text-[#64748B]">{v.vehicle_plates || '—'}</td>
                    <td className="py-3 text-[#64748B]">{v.vehicle_year || '—'}</td>
                    <td className="py-3 text-[#0055FF] text-xs font-medium truncate max-w-[180px]" title={v.client_name || ''}>{v.client_name || '—'}</td>
                    {allMonths.map((m) => {
                      const s = seriesByMonth[m];
                      if (!s) return <td key={m} className="py-3 pr-3 text-right text-[#CBD5E1]">—</td>;
                      return (
                        <td key={m} className={`py-3 pr-3 text-right ${s.alert ? 'bg-red-100/70' : ''}`} data-testid={s.alert ? `km-alert-cell-${v.vehicle_id}-${m}` : undefined}>
                          <div className={`font-semibold ${s.alert ? 'text-red-700' : 'text-[#0F172A]'}`}>
                            {s.alert && <WarningCircle size={12} weight="fill" className="inline mr-1 -mt-0.5" />}
                            {fmtKm(s.km)}
                          </div>
                          <div>{renderChange(s.pct_change, s.delta)}</div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Line chart */}
        <div className="card p-4 mb-6" data-testid="km-chart-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">
              <ChartLine size={16} className="text-[#7C3AED]" /> Evolución Mensual de Kilometraje
            </h3>
            <span className="text-xs text-[#94A3B8]">
              {chartVehicleIds.length} vehículo{chartVehicleIds.length === 1 ? '' : 's'} seleccionado{chartVehicleIds.length === 1 ? '' : 's'}
            </span>
          </div>

          {/* Vehicle chips selector */}
          <div className="flex flex-wrap gap-2 mb-4">
            {comparison.map((v, idx) => {
              const on = chartVehicleIds.includes(v.vehicle_id);
              const color = CHART_COLORS[idx % CHART_COLORS.length];
              return (
                <button
                  key={v.vehicle_id}
                  onClick={() => toggleChartVehicle(v.vehicle_id)}
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-medium transition-all"
                  style={{
                    borderColor: on ? color : '#E2E8F0',
                    backgroundColor: on ? `${color}15` : 'white',
                    color: on ? color : '#64748B',
                  }}
                  data-testid={`chart-toggle-${v.vehicle_id}`}
                >
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color, opacity: on ? 1 : 0.3 }} />
                  {v.vehicle_name || v.vehicle_plates || 'Sin nombre'} · {v.vehicle_plates}
                </button>
              );
            })}
          </div>

          {chartVehicles.length === 0 || chartData.length === 0 ? (
            <div className="text-center text-sm text-[#94A3B8] py-10">
              Selecciona uno o más vehículos para ver la gráfica.
            </div>
          ) : (
            <div style={{ width: '100%', height: 340 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 8, right: 20, bottom: 8, left: 10 }}>
                  <CartesianGrid stroke="#F1F5F9" strokeDasharray="4 4" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748B' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={(v) => fmtKm(v)} width={80} />
                  <Tooltip
                    formatter={(value) => [`${fmtKm(value)} km`, '']}
                    contentStyle={{ fontSize: 12, border: '1px solid #E2E8F0', borderRadius: 8 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {chartVehicles.map((v, idx) => {
                    const originalIdx = comparison.findIndex((c) => c.vehicle_id === v.vehicle_id);
                    const color = CHART_COLORS[originalIdx % CHART_COLORS.length];
                    const key = v.vehicle_name || v.vehicle_plates || v.vehicle_id;
                    return (
                      <Line
                        key={v.vehicle_id}
                        type="monotone"
                        dataKey={key}
                        stroke={color}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                        connectNulls
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        </>
      )}

      {/* Recent readings list */}
      <div className="card p-4">
        <h3 className="text-sm font-semibold text-[#0F172A] mb-3">Últimas Lecturas</h3>
        {readings.length === 0 ? (
          <p className="text-sm text-[#94A3B8]">Sin lecturas registradas.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-[#E2E8F0]">
                <tr className="text-left text-xs uppercase tracking-wider text-[#64748B]">
                  <th className="py-2">Fecha</th>
                  <th className="py-2">Vehículo</th>
                  <th className="py-2">Placas</th>
                  <th className="py-2">Año</th>
                  <th className="py-2">Cliente</th>
                  <th className="py-2 text-right">Kilometraje</th>
                  <th className="py-2">Notas</th>
                  <th className="py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {readings.slice(0, 100).map((r) => (
                  <tr key={r.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]">
                    <td className="py-2 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1 text-[#64748B]">
                        <CalendarBlank size={13} /> {r.date}
                      </span>
                    </td>
                    <td className="py-2">{r.vehicle_name || '—'}</td>
                    <td className="py-2 text-[#64748B]">{r.vehicle_plates || '—'}</td>
                    <td className="py-2 text-[#64748B]">{r.vehicle_year || '—'}</td>
                    <td className="py-2 text-[#0055FF] text-xs font-medium truncate max-w-[180px]" title={r.client_name || ''}>{r.client_name || '—'}</td>
                    <td className="py-2 text-right font-semibold">{fmtKm(r.km)}</td>
                    <td className="py-2 text-[#64748B] max-w-xs truncate">{r.notes || '—'}</td>
                    <td className="py-2 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => openEditModal(r)}
                          className="p-1.5 text-[#0055FF] hover:bg-[#EFF6FF] rounded"
                          title="Editar"
                          data-testid={`edit-km-${r.id}`}
                        >
                          <PencilSimple size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(r.id)}
                          className="p-1.5 text-[#E11D48] hover:bg-red-50 rounded"
                          title="Eliminar"
                          data-testid={`delete-km-${r.id}`}
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-[#E2E8F0]">
              <h2 className="text-lg font-heading font-bold">
                {editingId ? 'Editar Kilometraje' : 'Registrar Kilometraje'}
              </h2>
              <button onClick={() => { setShowModal(false); setEditingId(null); }}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {formError && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">{formError}</div>}
              <div>
                <label className="label">Vehículo *</label>
                <select
                  value={form.vehicle_id}
                  onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })}
                  className="input"
                  data-testid="km-vehicle-select"
                >
                  <option value="">Selecciona un vehículo</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.brand} {v.sub_brand} — {v.plates} ({v.year})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Fecha *</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="input"
                  data-testid="km-date-input"
                />
              </div>
              <div>
                <label className="label">Kilometraje actual *</label>
                <input
                  type="number"
                  value={form.km}
                  onChange={(e) => setForm({ ...form, km: e.target.value })}
                  min="0"
                  step="1"
                  placeholder="Ej: 82500"
                  className="input"
                  data-testid="km-value-input"
                />
              </div>
              <div>
                <label className="label">Notas</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="input min-h-[70px]"
                  placeholder="Ej: leído del odómetro al llenar tanque"
                />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => { setShowModal(false); setEditingId(null); }} className="btn-secondary flex-1">Cancelar</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1" data-testid="km-save-btn">
                  {saving ? 'Guardando…' : (editingId ? 'Actualizar' : 'Guardar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default KilometrajeTab;
